import { logger as config, LogLevel } from '../config';
import BrowserStorage from '../browserstorage/BrowserStorage';
import {
  AUTH_NAMESPACE,
  STORAGE_NAMESPACE_SESSION,
} from '../auth/providers/AuthProvider';
import pkg from '../../package.json';
import UAParser from 'ua-parser-js';

export enum LogSender {
  AJAX = 'Ajax',
  AUTH = 'Auth',
  BROWSERSTORAGE = 'BrowserStorage',
  REST = 'Rest',
  RESTPOLLING = 'RestPolling',
  STREAMING = 'Streaming',
  STREAMING_MONITOR = 'Streaming Monitor',
  STREAMING_MARKETDEPTH = 'MarketDepth-Streaming',
  STREAMING_NEWS = 'News-Streaming',
  STREAMING_QUOTE = 'Quote-Streaming',
  STREAMING_MARKETDATA = 'MarketData-Streaming',
  STREAMING_QUOTETRADE = 'Quote-Trade',
  USERDATA = 'UserData',
  CONFIG = 'Config',
}

class Logger {
  private getCurrentTime(): string {
    const pad = (num: number, places = 2) => String(num).padStart(places, '0');
    const date = new Date();
    return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
      date.getSeconds()
    )}.${pad(date.getMilliseconds(), 3)}`;
  }

  public get level() {
    return config.level;
  }

  public critical(sender: LogSender, ...message: unknown[]) {
    this._log(LogLevel.CRITICAL, sender, message);
  }
  public error(sender: LogSender, ...message: unknown[]) {
    this._log(LogLevel.ERROR, sender, message);
  }
  public warn(sender: LogSender, ...message: unknown[]) {
    this._log(LogLevel.WARNING, sender, message);
  }
  public info(sender: LogSender, ...message: unknown[]) {
    this._log(LogLevel.INFO, sender, message);
  }
  public verbose(sender: LogSender, ...message: unknown[]) {
    this._log(LogLevel.VERBOSE, sender, message);
  }
  public silly(sender: LogSender, ...message: unknown[]) {
    this._log(LogLevel.SILLY, sender, message);
  }
  public debug(sender: LogSender, ...message: unknown[]) {
    if (this.level >= LogLevel.INFO) {
      const parsedUA = new UAParser(navigator.userAgent).getResult();
      message.push(
        `\nDate: ${new Date().toLocaleDateString()}`,
        `\nTimezone Offset: ${new Date().getTimezoneOffset()}`,
        `\nSDK: v${pkg.version}`,
        `\nBrowser: ${JSON.stringify(parsedUA, null, 2)}`
      );
      const header = `[${this.getCurrentTime()} - ${sender}]`;
      BrowserStorage.getStorageObject(AUTH_NAMESPACE)
        .get(STORAGE_NAMESPACE_SESSION)
        .then((session) => {
          if (session) {
            message.push(
              `\nSession ID: ${session.sid}`,
              `\nUser Time Offset: ${session.userTimeOffset}`,
              `\nSession Latency: ${session.sessionLatency}`,
              `\nToken Latency: ${session.tokenLatency}`,
              `\nJWT: ${session.token}`
            );
            if (
              session.userTimeOffset < -15000 ||
              session.userTimeOffset > 60000
            ) {
              console.warn(
                header,
                `\nSystem time is off by ${session.userTimeOffset}ms`
              );
            }
          }
          console.debug(header, ...message);
        });
    }
  }

  private _log(level: LogLevel, sender: LogSender, message: unknown[]) {
    if (level <= this.level) {
      const header = `[${this.getCurrentTime()} - ${sender}]`;

      switch (level) {
        case LogLevel.CRITICAL:
        case LogLevel.ERROR:
          console.error(header, ...message);
          break;
        case LogLevel.WARNING:
          console.warn(header, ...message);
          break;
        case LogLevel.INFO:
          console.info(header, ...message);
          break;
        case LogLevel.VERBOSE:
        case LogLevel.SILLY:
          console.debug(header, ...message);
          break;
      }
    }
  }
}

const logger = new Logger();

export { logger as Logger };
